<template>
  <div class="auth-callback-page">
    <section class="auth-callback-card" aria-live="polite">
      <div v-if="!error" class="loading-spinner" aria-hidden="true"></div>
      <h1>{{ error ? 'Kirjautumista ei voitu viimeistellä' : 'Kirjaudutaan sisään' }}</h1>
      <p>{{ error || 'Vahvistetaan Google-kirjautuminen ja haetaan Raviremmi-tiliäsi...' }}</p>
      <button v-if="error" type="button" @click="returnToSignIn">Palaa kirjautumiseen</button>
    </section>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Auth, Hub } from 'aws-amplify';
import axios from '../../plugins/axios';

const RETRY_DELAY_MS = 250;
const MAX_AUTH_ATTEMPTS = 80;
const MAX_PROFILE_ATTEMPTS = 20;

const wait = (milliseconds) => new Promise(resolve => window.setTimeout(resolve, milliseconds));

const callbackErrorMessage = () => {
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get('error');
  if (!oauthError) return '';

  console.error('Cognito hosted UI returned an error:', oauthError);
  const description = params.get('error_description') || '';
  if (description.includes('Kirjaudu ensin kerran käyttäjänimelläsi ja salasanallasi')) {
    return 'Sinulla on jo Raviremmi-tili tällä sähköpostilla. Kirjaudu ensin kerran käyttäjänimelläsi ja salasanallasi. Sen jälkeen voit liittää Google-kirjautumisen tiliisi.';
  }
  return `Google-kirjautuminen hylättiin (${oauthError}). Palaa kirjautumiseen ja yritä uudelleen.`;
};

export default {
  name: 'AuthCallback',
  setup() {
    const error = ref('');
    const isFinishing = ref(false);
    const router = useRouter();

    const waitForAuthenticatedUser = async () => {
      let lastAuthError;
      let hostedUiFailure;
      let hostedUiSucceeded = false;
      const unsubscribe = Hub.listen('auth', ({ payload }) => {
        const event = payload?.event;
        if (event === 'signIn' || event === 'cognitoHostedUI') {
          hostedUiSucceeded = true;
        }
        if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
          hostedUiFailure = payload?.data || new Error(`Hosted UI event failed: ${event}`);
        }
      });

      try {
        for (let attempt = 0; attempt < MAX_AUTH_ATTEMPTS; attempt += 1) {
          if (hostedUiFailure) throw hostedUiFailure;
          try {
            return await Auth.currentAuthenticatedUser();
          } catch (authError) {
            lastAuthError = authError;
            await wait(hostedUiSucceeded ? 100 : RETRY_DELAY_MS);
          }
        }
        throw lastAuthError || new Error('Hosted UI session was not established');
      } finally {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      }
    };

    const finishAuthentication = async () => {
      if (isFinishing.value) return;
      error.value = '';
      const oauthError = callbackErrorMessage();
      if (oauthError) {
        error.value = oauthError;
        return;
      }

      isFinishing.value = true;
      try {
        try {
          await waitForAuthenticatedUser();
        } catch (authError) {
          console.error('Google OAuth session did not become available:', authError);
          error.value = 'Google-kirjautuminen ei valmistunut. Palaa kirjautumiseen ja yritä uudelleen.';
          return;
        }

        for (let attempt = 0; attempt < MAX_PROFILE_ATTEMPTS; attempt += 1) {
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/me`);
            if (response.data?.profile_setup_required) {
              await router.replace('/complete-google-registration');
            } else {
              await router.replace('/team');
            }
            return;
          } catch (profileError) {
            if (profileError?.response?.status === 404) {
              // First Google sign-in intentionally has no Player row until the
              // user has selected a public remmi name and accepted the terms.
              await router.replace('/complete-google-registration');
              return;
            }
            if (attempt === MAX_PROFILE_ATTEMPTS - 1) {
              error.value = 'Raviremmi-tilin hakeminen epäonnistui. Yritä hetken kuluttua uudelleen.';
              return;
            }
            await wait(RETRY_DELAY_MS);
          }
        }
      } finally {
        isFinishing.value = false;
      }
    };

    const returnToSignIn = async () => {
      await router.replace('/sign-in');
    };

    onMounted(finishAuthentication);

    return {
      error,
      finishAuthentication,
      returnToSignIn,
    };
  },
};
</script>

<style scoped>
.auth-callback-page {
  display: grid;
  min-height: 42vh;
  place-items: center;
  padding: 24px;
}

.auth-callback-card {
  width: min(100%, 460px);
  padding: 36px;
  text-align: center;
  background: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 10px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
}

.auth-callback-card h1 {
  margin: 18px 0 10px;
  color: #e6f1ff;
  font-size: 1.45rem;
}

.auth-callback-card p {
  margin: 0;
  color: #a8b2d1;
  line-height: 1.55;
}

.auth-callback-card button {
  margin-top: 22px;
  padding: 10px 18px;
  color: #0a192f;
  font-weight: 700;
  cursor: pointer;
  background: #64ffda;
  border: 0;
  border-radius: 4px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  margin: 0 auto;
  border: 3px solid rgba(100, 255, 218, 0.25);
  border-top-color: #64ffda;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
